import { useState } from "react";

import ChatIcon from "@mui/icons-material/ChatBubbleOutline";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  createPlanningComment,
  deletePlanningComment,
  listPlanningComments,
  patchPlanningComment,
} from "@/api/planningComments";
import { usePermission } from "@/auth/usePermission";
import { useAuthStore } from "@/store/authStore";

export function PlanningCommentsPanel({ planningId }: { planningId: string }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const canEdit = usePermission("planning:edit");
  const me = useAuthStore((s) => s.me);

  const list = useQuery({
    queryKey: ["planning-comments", planningId],
    queryFn: () => listPlanningComments(planningId),
  });

  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");

  const create = useMutation({
    mutationFn: (body: string) => createPlanningComment(planningId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["planning-comments", planningId] });
      setDraft("");
    },
  });

  const patch = useMutation({
    mutationFn: (vals: { id: string; body: string }) =>
      patchPlanningComment(planningId, vals.id, vals.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["planning-comments", planningId] });
      setEditingId(null);
      setEditBody("");
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deletePlanningComment(planningId, id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["planning-comments", planningId] }),
  });

  const rows = list.data ?? [];

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <ChatIcon fontSize="small" />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {t("planningComments.title")} ({rows.length})
        </Typography>
      </Stack>

      {list.isLoading && <CircularProgress size={20} />}

      {!list.isLoading && rows.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("planningComments.empty")}
        </Alert>
      )}

      <Stack spacing={1.5} sx={{ mb: canEdit ? 2 : 0 }}>
        {rows.map((c) => {
          const isAuthor = c.author_user_id === me?.id;
          const isEditing = editingId === c.id;
          return (
            <Paper
              key={c.id}
              variant="outlined"
              sx={{ p: 1.5, "&:hover .row-actions": { opacity: 1 } }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {c.author_full_name ?? c.author_email ?? t("planningComments.unknown")}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1, fontWeight: 400 }}
                  >
                    {new Date(c.created_at).toLocaleString(i18n.language)}
                    {c.updated_at !== c.created_at && ` · ${t("planningComments.edited")}`}
                  </Typography>
                </Typography>
                {canEdit && (
                  <Stack
                    direction="row"
                    spacing={0.25}
                    className="row-actions"
                    sx={{ opacity: 0, transition: "opacity 120ms" }}
                  >
                    {isAuthor && !isEditing && (
                      <Tooltip title={t("common.edit") ?? "Edit"}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingId(c.id);
                            setEditBody(c.body);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={t("common.delete") ?? "Delete"}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          if (confirm(t("planningComments.deleteConfirm"))) {
                            remove.mutate(c.id);
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                )}
              </Stack>
              {isEditing ? (
                <Stack spacing={1}>
                  <TextField
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={6}
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                    inputProps={{ maxLength: 1000 }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() =>
                        patch.mutate({ id: c.id, body: editBody })
                      }
                      disabled={!editBody.trim() || patch.isPending}
                    >
                      {t("common.save")}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setEditingId(null);
                        setEditBody("");
                      }}
                    >
                      {t("common.cancel")}
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", color: "text.primary" }}
                >
                  {c.body}
                </Typography>
              )}
            </Paper>
          );
        })}
      </Stack>

      {canEdit && (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            size="small"
            multiline
            minRows={1}
            maxRows={5}
            fullWidth
            placeholder={t("planningComments.placeholder")}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            inputProps={{ maxLength: 1000 }}
          />
          <Button
            variant="contained"
            onClick={() => create.mutate(draft.trim())}
            disabled={!draft.trim() || create.isPending}
            sx={{ alignSelf: "flex-start" }}
          >
            {t("planningComments.post")}
          </Button>
        </Stack>
      )}
    </Box>
  );
}
